import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, getMe } from '../api/client';
import { useTokenSource } from '../api/token';
import { useExportStore } from '../session/export';

/**
 * Account dashboard (card t_31859fc7 §5 and §6).
 *
 * Three jobs, and nothing more: show what the account is using against the free-tier limits, let
 * the coach browse their clips by season → session, and give them a way out (deletion).
 *
 * The retention warning and the upgrade prompt both come from the worker's numbers
 * (`GET /api/user/usage`), not from constants copied into the SPA — a limit that drifts between
 * client and server is a limit that lies to the coach.
 */
function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 MB';
  const mb = bytes / (1024 * 1024);
  if (mb < 1) return `${(bytes / 1024).toFixed(0)} KB`;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
}

export function AccountRoute() {
  const tokens = useTokenSource();
  const usage = useExportStore((state) => state.usage);
  const usageError = useExportStore((state) => state.usageError);
  const shares = useExportStore((state) => state.shares);
  const sharesError = useExportStore((state) => state.sharesError);
  const reels = useExportStore((state) => state.reels);
  const seasons = useExportStore((state) => state.seasons);
  const browsedClips = useExportStore((state) => state.browsedClips);
  const browseSeason = useExportStore((state) => state.browseSeason);
  const browseLoading = useExportStore((state) => state.browseLoading);
  const loadUsage = useExportStore((state) => state.loadUsage);
  const loadShares = useExportStore((state) => state.loadShares);
  const loadReels = useExportStore((state) => state.loadReels);
  const loadSeasons = useExportStore((state) => state.loadSeasons);
  const loadBrowse = useExportStore((state) => state.loadBrowse);
  const revoke = useExportStore((state) => state.revoke);
  const deleteAccount = useExportStore((state) => state.deleteAccount);
  const runRetention = useExportStore((state) => state.runRetention);

  const [token, setToken] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletionResult, setDeletionResult] = useState<string | null>(null);
  const [retentionResult, setRetentionResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void tokens.getToken().then((value) => {
      if (!cancelled) setToken(value);
    });
    return () => {
      cancelled = true;
    };
  }, [tokens]);

  useEffect(() => {
    // The whole screen is worker-backed; without a token there is nothing honest to show.
    if (!token) return;
    void loadUsage(token);
    void loadShares(token);
    void loadReels(token);
    void loadSeasons(token);
    void loadBrowse(token, { season: null });
    void getMe(token)
      .then((me) => setEmail(me.user?.email ?? null))
      .catch((error: unknown) => {
        if (error instanceof ApiError) setEmail(null);
      });
  }, [token, loadUsage, loadShares, loadReels, loadSeasons, loadBrowse]);

  const onDelete = useCallback(async () => {
    setBusy(true);
    const result = await deleteAccount(token);
    setDeletionResult(result.detail);
    setConfirmOpen(false);
    setBusy(false);
  }, [deleteAccount, token]);

  const onRetention = useCallback(async () => {
    setBusy(true);
    const result = await runRetention(token);
    setRetentionResult(result.detail);
    setBusy(false);
  }, [runRetention, token]);

  const usageNumbers = usage?.usage;

  return (
    <div className="space-y-6" data-testid="account-page">
      <header>
        <h1 className="text-xl font-semibold tracking-tight">Account</h1>
        <p className="mt-1 text-sm text-charcoal-500">
          What Callout is storing for you, what your plan allows, and how to delete it.
        </p>
      </header>

      {usageError && (
        <p role="alert" className="text-sm font-medium text-red-700" data-testid="account-error">
          {usageError}
        </p>
      )}

      <section
        data-testid="usage-panel"
        data-tier={usage?.tier ?? 'unknown'}
        data-watermark={usage?.subscription?.watermark ? 'true' : 'false'}
        className="rounded-lg border border-charcoal-500/20 bg-white/60 p-4"
      >
        <h2 className="text-sm font-semibold uppercase tracking-wide text-charcoal-500">Usage</h2>
        {usageNumbers ? (
          <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-2 font-mono text-xs text-charcoal-700 sm:grid-cols-3">
            <p data-testid="usage-storage">storage {formatBytes(usageNumbers.storageBytes)}</p>
            <p data-testid="usage-clips">
              clips {usageNumbers.clipsTotal} ({usageNumbers.clipsConfirmed} confirmed)
            </p>
            <p data-testid="usage-sessions">
              sessions {usageNumbers.sessionsTotal}
              {` · this week ${usageNumbers.sessionsThisWeek}/${usageNumbers.sessionsThisWeekLimit}`}
            </p>
            <p data-testid="usage-footage">
              footage {usageNumbers.footageCount} file(s) ·{' '}
              {(usageNumbers.footageSeconds / 60).toFixed(1)} min
            </p>
            <p data-testid="usage-reels">
              reels this week {usageNumbers.reelsThisWeek}/{usageNumbers.reelsPerWeekLimit}
            </p>
            <p data-testid="usage-shares">
              share links {usageNumbers.activeShareLinks}/{usageNumbers.activeShareLinkLimit}
            </p>
          </div>
        ) : (
          <p className="mt-2 text-xs text-charcoal-500" data-testid="usage-pending">
            Loading usage…
          </p>
        )}

        {usage?.retention && (
          <p
            data-testid="retention-warning"
            className={`mt-3 rounded px-3 py-2 text-xs ${
              usage.retention.expiringNow > 0
                ? 'bg-copper-300/30 text-charcoal-900'
                : 'text-charcoal-600'
            }`}
          >
            {usage.retention.warning}
            {usage.retention.expiringNow > 0
              ? ` ${usage.retention.expiringNow} item(s) are due for deletion now.`
              : ''}
          </p>
        )}

        {usage && !usage.subscription.proAvailable && (
          <p className="mt-2 text-xs text-charcoal-500" data-testid="upgrade-note">
            {usage.subscription.upgradeMessage}
          </p>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button
            type="button"
            data-testid="run-retention"
            disabled={busy || !token}
            onClick={() => void onRetention()}
            className="rounded border border-charcoal-500/30 px-3 py-1.5 text-xs font-semibold text-charcoal-700 disabled:opacity-50"
          >
            Run retention pass
          </button>
          <Link to="/privacy" className="text-xs text-copper-700 underline" data-testid="account-privacy-link">
            Privacy policy
          </Link>
          {retentionResult && (
            <span className="font-mono text-xs text-charcoal-500" data-testid="retention-result">
              {retentionResult}
            </span>
          )}
        </div>
      </section>

      <section
        data-testid="browse-panel"
        data-season={browseSeason ?? 'all'}
        className="rounded-lg border border-charcoal-500/20 bg-white/60 p-4"
      >
        <h2 className="text-sm font-semibold uppercase tracking-wide text-charcoal-500">
          Your clips by season
        </h2>
        <div className="mt-2 flex flex-wrap gap-2" data-testid="season-list">
          <button
            type="button"
            data-testid="season-all"
            onClick={() => void loadBrowse(token, { season: null })}
            className={`rounded px-2.5 py-1 text-xs font-semibold ${
              browseSeason === null ? 'bg-charcoal text-chalk' : 'border border-charcoal-500/30 text-charcoal-700'
            }`}
          >
            All
          </button>
          {seasons.map((entry) => (
            <button
              key={entry.season}
              type="button"
              data-testid="season-chip"
              data-season={entry.season}
              data-clips={entry.clips}
              onClick={() => void loadBrowse(token, { season: entry.season })}
              className={`rounded px-2.5 py-1 text-xs font-semibold ${
                browseSeason === entry.season
                  ? 'bg-charcoal text-chalk'
                  : 'border border-charcoal-500/30 text-charcoal-700'
              }`}
            >
              {entry.season} · {entry.clips} clip{entry.clips === 1 ? '' : 's'}
            </button>
          ))}
        </div>

        {browseLoading && (
          <p className="mt-3 text-xs text-charcoal-500" data-testid="browse-loading">
            Loading clips…
          </p>
        )}
        {!browseLoading && browsedClips.length === 0 && (
          <p className="mt-3 text-xs text-charcoal-500" data-testid="browse-empty">
            No clips yet. Tag a session and confirm a clip in review.
          </p>
        )}
        {browsedClips.length > 0 && (
          <table className="mt-3 w-full text-left text-xs" data-testid="browse-table">
            <thead className="text-charcoal-500">
              <tr>
                <th className="py-1">Season</th>
                <th className="py-1">Session</th>
                <th className="py-1">Clip</th>
                <th className="py-1">Window</th>
                <th className="py-1">Status</th>
              </tr>
            </thead>
            <tbody className="font-mono text-charcoal-700">
              {browsedClips.map((clip) => (
                <tr key={clip.id} data-testid="browse-row" data-clip-id={clip.id} data-season={clip.season}>
                  <td className="py-1" data-testid="browse-season">
                    {clip.season}
                  </td>
                  <td className="py-1" data-testid="browse-session">
                    {clip.session_id}
                  </td>
                  <td className="py-1" data-testid="browse-label">
                    {clip.edited_label ?? clip.label}
                  </td>
                  <td className="py-1">
                    {clip.t_in.toFixed(1)}s–{(clip.edited_t_out ?? clip.t_out).toFixed(1)}s
                  </td>
                  <td className="py-1" data-testid="browse-status">
                    {clip.status}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="rounded-lg border border-charcoal-500/20 bg-white/60 p-4" data-testid="shares-panel">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-charcoal-500">
          Share links
        </h2>
        {sharesError && (
          <p className="mt-2 text-xs text-red-700" data-testid="shares-error">
            {sharesError}
          </p>
        )}
        {shares.length === 0 ? (
          <p className="mt-2 text-xs text-charcoal-500" data-testid="shares-empty">
            No share links yet. Render a clip on the export screen and share it.
          </p>
        ) : (
          <ul className="mt-2 space-y-2">
            {shares.map((share) => (
              <li
                key={share.id}
                data-testid="share-row"
                data-token={share.token}
                data-access-count={share.accessCount}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-charcoal-500/10 pb-2 text-xs"
              >
                <span className="font-semibold" data-testid="share-row-title">
                  {share.title ?? share.kind}
                </span>
                <span className="rounded bg-charcoal-500/10 px-1.5 py-0.5 font-mono">{share.kind}</span>
                <span className="font-mono text-charcoal-500" data-testid="share-row-count">
                  {share.accessCount} view{share.accessCount === 1 ? '' : 's'}
                </span>
                <a
                  className="text-copper-700 underline"
                  href={share.urls.pageUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="share-row-link"
                >
                  open
                </a>
                <button
                  type="button"
                  data-testid="share-row-revoke"
                  onClick={() => void revoke(share.id, token)}
                  className="ml-auto rounded border border-charcoal-500/30 px-2 py-1 font-semibold text-charcoal-700"
                >
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-lg border border-charcoal-500/20 bg-white/60 p-4" data-testid="reels-panel">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-charcoal-500">
          Highlight reels
        </h2>
        {reels.length === 0 ? (
          <p className="mt-2 text-xs text-charcoal-500" data-testid="reels-empty">
            No reels yet. Select 3 or more confirmed clips on the export screen.
          </p>
        ) : (
          <ul className="mt-2 space-y-2 text-xs">
            {reels.map((reel) => (
              <li
                key={reel.id}
                data-testid="reel-row"
                data-token={reel.token}
                data-clip-count={reel.clipIds.length}
                className="border-b border-charcoal-500/10 pb-2"
              >
                <span className="font-semibold">{reel.title}</span> · {reel.clipIds.length} clips
                {reel.media.storageKey ? ' · ready' : ' · no media yet'}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section
        className="rounded-lg border border-red-300 bg-red-50/60 p-4"
        data-testid="danger-panel"
      >
        <h2 className="text-sm font-semibold uppercase tracking-wide text-red-700">Delete account</h2>
        <p className="mt-2 text-xs text-charcoal-700">
          This deletes your footage, audio, clips, highlight reels and share links from storage and
          from the database. It cannot be undone.
        </p>
        <button
          type="button"
          data-testid="delete-account"
          disabled={busy || !token}
          onClick={() => setConfirmOpen(true)}
          className="mt-3 rounded bg-red-700 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
        >
          Delete account
        </button>

        {confirmOpen && (
          <div
            role="dialog"
            aria-modal="true"
            data-testid="delete-confirm-dialog"
            className="mt-3 rounded border border-red-400 bg-white p-3"
          >
            <p className="text-xs text-charcoal-800">
              Delete <strong>{email ?? 'this account'}</strong> and everything in it? This is the
              confirmation dialog; the worker also requires <code className="font-mono">?confirm=delete</code>.
            </p>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                data-testid="delete-confirm"
                disabled={busy}
                onClick={() => void onDelete()}
                className="rounded bg-red-700 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
              >
                Yes, delete everything
              </button>
              <button
                type="button"
                data-testid="delete-cancel"
                onClick={() => setConfirmOpen(false)}
                className="rounded border border-charcoal-500/30 px-3 py-1.5 text-xs font-semibold text-charcoal-700"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {deletionResult && (
          <p className="mt-3 text-xs text-charcoal-800" data-testid="delete-result">
            {deletionResult}
          </p>
        )}
      </section>
    </div>
  );
}
